import { useEffect, useState, type ReactNode } from "react";

export function LoadingState({
  label = "charts",
  compact = false,
}: {
  label?: string;
  compact?: boolean;
}): ReactNode {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), 3_000);
    return () => clearTimeout(timer);
  }, []);
  return (
    <section
      className={`loading-view${compact ? " loading-view--compact" : ""}`}
      role="status"
      aria-label={`Loading ${label}`}
      aria-busy="true"
    >
      <div className="loading-view__label">
        <span className="loading-spinner" aria-hidden="true" />
        Loading {label}…
      </div>
      {slow ? (
        <p className="loading-view__slow">
          Still loading. You can change the dates or switch tabs.
        </p>
      ) : null}
      {!compact ? (
        <div className="loading-view__cards" aria-hidden="true">
          {[0, 1, 2].map((n) => (
            <div key={n}>
              <i />
              <i />
            </div>
          ))}
        </div>
      ) : null}
      <div className="loading-view__chart" aria-hidden="true">
        <i />
        <i />
        <i />
      </div>
    </section>
  );
}

interface QueryState {
  data?: unknown;
  error: unknown;
  isFetching: boolean;
  isPlaceholderData: boolean;
  fetchStatus: string;
  refetch(): unknown;
}
export function QueryNotice({
  query,
  label,
  showInitial = false,
}: {
  query: QueryState;
  label: string;
  showInitial?: boolean;
}): ReactNode {
  const hasData = query.data !== undefined;
  const meta = typeof query.data === "object" && query.data !== null && "meta" in query.data
    ? query.data.meta : null;
  const serverState = meta && typeof meta === "object" && "refreshState" in meta ? meta.refreshState : undefined;
  const serverFailed = serverState === "failed";
  const serverRefreshing = serverState === "refreshing";
  // Routine refresh belongs in the reserved header indicator, not a new row.
  if (hasData && query.isFetching && !query.isPlaceholderData && !query.error && !serverFailed)
    return null;
  if (
    (!hasData && !showInitial) ||
    (!query.isFetching && !query.error && query.fetchStatus !== "paused" && !serverFailed && !serverRefreshing)
  )
    return null;
  const failed = (!!query.error || serverFailed) && !query.isFetching;
  const offline = query.fetchStatus === "paused";
  const previous = query.isPlaceholderData;
  const computed =
    meta && typeof meta === "object" && "computedAt" in meta
      ? String(meta.computedAt)
      : "";
  const validTime = Number.isFinite(Date.parse(computed));
  return (
    <div
      className={`query-notice${failed || offline ? " query-notice--warning" : ""}`}
      role="status"
      aria-live="polite"
      data-previous-selection={previous || undefined}
    >
      {query.isFetching || serverRefreshing ? (
        <span className="loading-spinner" aria-hidden="true" />
      ) : null}
      <span>
        <strong>
          {label} ·{" "}
          {offline
            ? "Offline"
            : failed
              ? hasData
                ? "Could not update"
                : "Could not load"
              : hasData
                ? "Updating…"
                : "Loading…"}
        </strong>{" "}
        {hasData
          ? previous
            ? "Showing the previous selection."
            : "Showing saved data."
          : null}
        {validTime ? (
          <>
            {" "}
            Last checked{" "}
            <time dateTime={computed}>
              {new Date(computed).toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
                timeZone: "UTC",
                timeZoneName: "short",
              })}
            </time>
            .
          </>
        ) : null}
      </span>
      {failed ? (
        <button
          type="button"
          className="button"
          onClick={() => void query.refetch()}
        >
          Try again
        </button>
      ) : null}
    </div>
  );
}
